// src/components/TrainerDashboard/GroupsPanel.js
import React, { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { useTranslation } from '../../i18n';

// ====== ICONOS desde src/assets/icons ======
import iconLogo from '../../assets/icons/logo.png';
import iconRun from '../../assets/icons/running.png';
import iconBike from '../../assets/icons/cycling.png';
import iconSwim from '../../assets/icons/swim.png';
import iconTri from '../../assets/icons/triathlon.png';
import iconStrength from '../../assets/icons/strength.png';
import iconOther from '../../assets/icons/other.png';

// Modal para aplicar plan al grupo
import GroupApplyPlanModal from './GroupApplyPlanModal';
import API_BASE_URL from '../../config/api';

// ====== ENV / API ======
const ENV_BASE =
  process.env.REACT_APP_API_BASE ||
  (typeof window !== 'undefined' && window.__CONFIG__ && window.__CONFIG__.API_BASE) ||
  `${API_BASE_URL}/api`;

const ENV_HOST =
  process.env.REACT_APP_HOST ||
  (typeof window !== 'undefined' && window.__CONFIG__ && window.__CONFIG__.HOST) ||
  API_BASE_URL;

const HOST = ENV_HOST;
const DEFAULT_AVATAR = `${HOST}/uploads/athletes/default.jpg`;

const api = axios.create({
  baseURL: ENV_BASE,
  withCredentials: true,
  timeout: 15000,
});

// Normaliza rutas antiguas '/trainer/...'
api.interceptors.request.use((cfg) => {
  if (typeof cfg.url === 'string') {
    cfg.url = cfg.url.replace(/^\/trainer\//, '/trainers/');
  }
  return cfg;
});

// ====== Utils ======
const pickFirst = (...vals) => vals.find((v) => v != null);

const normSport = (v) => {
  const s = String(v || '').toLowerCase();
  if (['run', 'running', 'correr'].includes(s)) return 'correr';
  if (['bike', 'cycling', 'ciclismo'].includes(s)) return 'ciclismo';
  if (['swim', 'swimming', 'nadar', 'natacion', 'nataci�n'].includes(s)) return 'nadar';
  if (['tri', 'triatlon', 'triatl�n', 'triathlon'].includes(s)) return 'triatlon';
  if (['strength', 'fuerza', 'gym', 'gimnasio'].includes(s)) return 'fuerza';
  if (!s || s === 'all' || s === 'todos') return 'all';
  return 'otro';
};

const looksLikeImage = (s) => {
  if (!s || typeof s !== 'string') return false;
  const v = s.trim().toLowerCase();
  return (
    v.startsWith('data:image/') ||
    v.includes('/uploads/') ||
    /\.(png|jpg|jpeg|gif|webp|bmp|svg)(\?.*)?$/.test(v) ||
    /^https?:\/\//.test(v)
  );
};

const resolveAthleteAvatar = (a) => {
  const cands = [
    a.__photoResolved,
    a.profilePhoto,
    a.profilePicture,
    a.photoUrl,
    a.avatarUrl,
    a.avatar,
    a.imageUrl,
    a.picture,
    a.photo,
    a?.athlete?.profilePhoto,
    a?.athlete?.photoUrl,
    a?.user?.photoUrl,
  ];
  for (const raw of cands) {
    const v = (raw ?? '').toString().trim();
    if (!v) continue;
    if (v.startsWith('http')) return v;
    if (v.startsWith('/')) return `${HOST}${v}`;
    if (looksLikeImage(v)) return `${HOST}/uploads/athletes/${v}`;
  }
  return DEFAULT_AVATAR;
};

// ====== Iconos por deporte ======
const ICON_BY_SPORT = {
  all: iconLogo,
  correr: iconRun,
  ciclismo: iconBike,
  nadar: iconSwim,
  triatlon: iconTri,
  fuerza: iconStrength,
  otro: iconOther,
};

// ====== API helpers con fallbacks (prioriza /trainers) ======
async function getGroups(trainerId, signal) {
  const tries = [
    () => api.get(`/trainers/${trainerId}/groups`, { signal }),
    () => api.get(`/groups`, { params: { trainerId }, signal }),
    () => api.get(`/groups/list`, { params: { trainerId }, signal }),
    () => api.get(`/trainer/${trainerId}/groups`, { signal }),
    () => api.get(`/trainer/groups`, { params: { trainerId }, signal }),
  ];
  for (const t of tries) {
    try {
      const { data } = await t();
      return Array.isArray(data) ? data : Array.isArray(data?.groups) ? data.groups : [];
    } catch {}
  }
  return [];
}

async function createGroup(trainerId, payload) {
  const tries = [
    () => api.post(`/trainers/${trainerId}/groups`, payload),
    () => api.post(`/groups`, { ...payload, trainerId }),
    () => api.post(`/trainer/${trainerId}/groups`, payload),
    () => api.post(`/trainer/groups`, { ...payload, trainerId }),
  ];
  for (const t of tries) {
    try {
      return await t();
    } catch {}
  }
  throw new Error('No hay endpoint para crear grupos');
}

async function updateGroup(trainerId, groupId, payload) {
  const tries = [
    () => api.put(`/trainers/${trainerId}/groups/${groupId}`, payload),
    () => api.patch(`/trainers/${trainerId}/groups/${groupId}`, payload),
    () => api.put(`/groups/${groupId}`, { ...payload, trainerId }),
    () => api.patch(`/groups/${groupId}`, { ...payload, trainerId }),
    () => api.put(`/trainer/${trainerId}/groups/${groupId}`, payload),
    () => api.patch(`/trainer/${trainerId}/groups/${groupId}`, payload),
    () => api.post(`/groups/update`, { id: groupId, trainerId, ...payload }),
  ];
  for (const t of tries) {
    try {
      return await t();
    } catch {}
  }
  throw new Error('No hay endpoint para actualizar grupos');
}

async function deleteGroup(trainerId, groupId) {
  const tries = [
    () => api.delete(`/trainers/${trainerId}/groups/${groupId}`),
    () => api.delete(`/groups/${groupId}`, { data: { trainerId } }),
    () => api.delete(`/trainer/${trainerId}/groups/${groupId}`),
    () => api.post(`/groups/delete`, { id: groupId, trainerId }),
  ];
  for (const t of tries) {
    try {
      return await t();
    } catch {}
  }
  throw new Error('No hay endpoint para eliminar grupos');
}

// ====== Componente ======
const GroupsPanel = ({
  trainerId = '',
  athletes = [],
  onSelect = () => {},
  standalone = false,
}) => {
  const { t } = useTranslation();

  const SPORTS = [
    { key: 'all', label: t('groupsPanel.all'), icon: ICON_BY_SPORT.all },
    { key: 'correr', label: t('groupsPanel.run'), icon: ICON_BY_SPORT.correr },
    { key: 'ciclismo', label: t('groupsPanel.cycling'), icon: ICON_BY_SPORT.ciclismo },
    { key: 'nadar', label: t('groupsPanel.swim'), icon: ICON_BY_SPORT.nadar },
    { key: 'triatlon', label: t('groupsPanel.triathlon'), icon: ICON_BY_SPORT.triatlon },
    { key: 'fuerza', label: t('groupsPanel.strength'), icon: ICON_BY_SPORT.fuerza },
    { key: 'otro', label: t('groupsPanel.other'), icon: ICON_BY_SPORT.otro },
  ];

  const [filter, setFilter] = useState('all');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [groups, setGroups] = useState([]);
  const [selected, setSelected] = useState(null);
  const [search, setSearch] = useState('');

  const [formKey, setFormKey] = useState(0);
  const [mode, setMode] = useState('view');
  const [form, setForm] = useState({
    _id: null,
    name: '',
    sport: 'correr',
    description: '',
    members: [],
  });
  const [saving, setSaving] = useState(false);
  const [savedMsg, setSavedMsg] = useState('');

  const [applyResult, setApplyResult] = useState(null);

  const didAutopickRef = useRef(false);

  const [openDropdown, setOpenDropdown] = useState(false);
  const [activeIdx, setActiveIdx] = useState(0);
  const searchRef = useRef(null);
  const dropdownRef = useRef(null);

  const [showAllAthletes, setShowAllAthletes] = useState(false);

  const [showApply, setShowApply] = useState(false);

  const load = async (signal) => {
    if (!trainerId) return;
    setLoading(true);
    setError('');
    try {
      const rows = await getGroups(trainerId, signal);
      const norm = rows.map((g) => ({
        _id: pickFirst(g._id, g.id, g.groupId, g.group_id) || Math.random().toString(36).slice(2),
        name: (g.name || g.title || t('groupsPanel.defaultGroup')).trim(),
        sport: normSport(g.sport || g.type),
        description: g.description || '',
        members: Array.isArray(g.members)
          ? g.members.map(String)
          : Array.isArray(g.athletes)
            ? g.athletes.map((a) => String(a?._id || a))
            : [],
        createdAt: g.createdAt || g.created_at || null,
        updatedAt: g.updatedAt || g.updated_at || null,
      }));
      setGroups(norm);
      if (!didAutopickRef.current && mode !== 'create' && !selected && norm.length) {
        handleSelect(norm[0]);
        didAutopickRef.current = true;
      }
    } catch {
      setError(t('groupsPanel.errorLoadingGroups'));
      setGroups([]);
      setSelected(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const ctrl = new AbortController();
    load(ctrl.signal);
    return () => ctrl.abort();
  }, [trainerId]); // eslint-disable-line

  const athIdx = useMemo(() => {
    const m = new Map();
    athletes.forEach((a) => {
      const id = String(a._id || a.id || '');
      if (!id) return;
      const name =
        `${a.firstName || ''} ${a.lastName || ''}`.trim() ||
        a.name ||
        t('groupsPanel.athlete');
      m.set(id, { id, name, avatar: resolveAthleteAvatar(a) });
    });
    return m;
  }, [athletes, t]);

  const membersDetailed = useMemo(
    () => (form.members || []).map((id) => athIdx.get(String(id))).filter(Boolean),
    [form.members, athIdx]
  );

  const allAthletes = useMemo(() => {
    const list = athletes
      .map((a) => {
        const id = String(a._id || a.id || '');
        const name =
          `${a.firstName || ''} ${a.lastName || ''}`.trim() ||
          a.name ||
          t('groupsPanel.athlete');
        return id ? { id, name, avatar: resolveAthleteAvatar(a) } : null;
      })
      .filter(Boolean)
      .sort((a, b) => a.name.localeCompare(b.name, 'es', { sensitivity: 'base' }));
    return list;
  }, [athletes, t]);

  const addAllMembers = () =>
    setForm((f) => ({ ...f, members: Array.from(new Set([...(f.members || []), ...allAthletes.map((a) => a.id)])) }));

  const clearAllMembers = () =>
    setForm((f) => ({ ...f, members: [] }));

  const handleSelect = (g) => {
    setSelected(g);
    onSelect(g);
    setForm({
      _id: g._id,
      name: g.name || '',
      sport: normSport(g.sport || 'correr'),
      description: g.description || '',
      members: Array.isArray(g.members) ? g.members.map(String) : [],
    });
    setSavedMsg('');
    setShowAllAthletes(false);
    setSearch('');
    setOpenDropdown(false);
    setActiveIdx(0);
    setApplyResult(null);
  };

  const startCreate = (e) => {
    if (e?.preventDefault) e.preventDefault();
    if (e?.stopPropagation) e.stopPropagation();
    setMode('create');
    setSelected(null);
    setFormKey((k) => k + 1);
    setForm({
      _id: null,
      name: '',
      sport: 'correr',
      description: '',
      members: [],
    });
    setSavedMsg('');
    setShowAllAthletes(false);
    setSearch('');
    setOpenDropdown(false);
    setActiveIdx(0);
    setApplyResult(null);
  };

  const isDirty = useMemo(() => {
    if (!selected && !form._id) {
      return form.name || form.description || (form.members || []).length > 0 || form.sport !== 'correr';
    }
    if (!selected) return true;
    const s = selected;
    return (
      (form.name || '').trim() !== (s.name || '').trim() ||
      normSport(form.sport) !== normSport(s.sport) ||
      (form.description || '').trim() !== (s.description || '').trim() ||
      JSON.stringify((form.members || []).map(String).sort()) !== JSON.stringify((s.members || []).map(String).sort())
    );
  }, [form, selected]);

  const save = async () => {
    if (saving) return;
    const payload = {
      name: (form.name || '').trim() || t('groupsPanel.defaultGroup'),
      sport: normSport(form.sport || 'correr'),
      description: (form.description || '').trim(),
      members: (form.members || []).map(String),
    };

    try {
      setSaving(true);
      if (form._id) {
        await updateGroup(trainerId, form._id, payload);
        setGroups((prev) => prev.map((g) => (g._id === form._id ? { ...g, ...payload, _local: false } : g)));
        setSelected((prev) => (prev ? { ...prev, ...payload, _local: false } : prev));
      } else {
        const res = await createGroup(trainerId, payload).catch(() => null);
        if (res?.data?._id || res?.data?.id) {
          const newId = String(res.data._id || res.data.id);
          const g = { _id: newId, ...payload };
          setGroups((prev) => [g, ...prev]);
          setMode('view');
          handleSelect(g);
        } else {
          const localId = `local-${Math.random().toString(36).slice(2)}`;
          const g = { _id: localId, ...payload, _local: true };
          setGroups((prev) => [g, ...prev]);
          setMode('view');
          handleSelect(g);
          alert(t('groupsPanel.localGroupCreated'));
        }
      }
      setSavedMsg(t('groupsPanel.savedChanges'));
      setTimeout(() => setSavedMsg(''), 2500);
    } catch (e) {
      console.error(e);
      alert(t('groupsPanel.saveError'));
    } finally {
      setSaving(false);
    }
  };

  const saveAsNew = async () => {
    const clone = { ...form, _id: null };
    setForm(clone);
    await save();
  };

  const handleDelete = async () => {
    if (!form._id) return;
    const ok = window.confirm(t('groupsPanel.confirmDelete'));
    if (!ok) return;
    try {
      await deleteGroup(trainerId, form._id);
      setGroups((prev) => prev.filter((g) => g._id !== form._id));
      setSelected(null);
      setForm({ _id: null, name: '', sport: 'correr', description: '', members: [] });
      setSavedMsg('');
      setShowAllAthletes(false);
      setSearch('');
      setOpenDropdown(false);
      setActiveIdx(0);
      setApplyResult(null);
    } catch (e) {
      console.error(e);
      alert(t('groupsPanel.deleteError'));
    }
  };

  const addMember = (ath) => {
    if (!ath) return;
    const id = String(ath.id || ath._id || '');
    if (!id) return;
    setForm((f) => (f.members.includes(id) ? f : { ...f, members: [...f.members, id] }));
  };

  const removeMember = (id) => {
    setForm((f) => ({ ...f, members: (f.members || []).filter((x) => String(x) !== String(id)) }));
  };

  const candidates = useMemo(() => {
    const q = search.trim().toLowerCase();
    const exclude = new Set((form.members || []).map(String));
    const base = athletes
      .map((a) => {
        const id = String(a._id || a.id || '');
        const name =
          `${a.firstName || ''} ${a.lastName || ''}`.trim() ||
          a.name ||
          t('groupsPanel.athlete');
        return { id, name, avatar: resolveAthleteAvatar(a) };
      })
      .filter((x) => x.id && !exclude.has(x.id));
    const arr = q ? base.filter((x) => x.name.toLowerCase().includes(q)) : base;
    return arr.slice(0, 10);
  }, [athletes, form.members, search, t]);

  useEffect(() => {
    const onDocClick = (e) => {
      if (!dropdownRef.current) return;
      if (!dropdownRef.current.contains(e.target) && !searchRef.current?.contains(e.target)) {
        setOpenDropdown(false);
      }
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  const onSearchKey = (e) => {
    if (!openDropdown && ['ArrowDown', 'ArrowUp', 'Enter'].includes(e.key)) {
      setOpenDropdown(true);
    }
    if (!openDropdown) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIdx((i) => Math.min((i ?? 0) + 1, Math.max(0, candidates.length - 1)));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIdx((i) => Math.max((i ?? 0) - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const chosen = candidates[activeIdx] || candidates[0];
      if (chosen) {
        addMember(chosen);
        setSearch('');
        setOpenDropdown(false);
        setActiveIdx(0);
      }
    } else if (e.key === 'Escape') {
      setOpenDropdown(false);
    }
  };

  const onMembersDragOver = (e) => {
    const types = e?.dataTransfer?.types || [];
    if (
      types.includes('application/x-tzone-athlete') ||
      types.includes('application/json') ||
      types.includes('text/plain')
    ) {
      e.preventDefault();
    }
  };

  const onMembersDrop = (e) => {
    try {
      const types = e?.dataTransfer?.types || [];
      const get = (tpe) => (types.includes(tpe) ? e.dataTransfer.getData(tpe) : '');
      let raw = get('application/x-tzone-athlete') || get('application/json') || get('text/plain');
      if (!raw) return;
      let id = raw;
      try {
        const obj = JSON.parse(raw);
        id = obj?.id || obj?._id || obj?.athleteId || obj?.athlete_id || raw;
      } catch {}
      id = String(id).trim();
      if (!id) return;
      const found = athletes.find((a) => String(a._id || a.id) === id);
      if (found) addMember({ id, ...found });
    } catch {}
  };

  const filtered = useMemo(() => {
    const k = normSport(filter);
    if (k === 'all') return groups;
    return groups.filter((g) => normSport(g.sport) === k);
  }, [groups, filter]);

  const renderSportIcon = (sportKey, size = 16) => (
    <img
      src={ICON_BY_SPORT[normSport(sportKey)] || iconOther}
      alt={sportKey}
      width={size}
      height={size}
      style={{ objectFit: 'contain' }}
    />
  );

  return (
    <div
      className={`groups-panel ${standalone ? 'groups-panel--standalone' : 'td-card'}`}
      style={standalone ? { overflow: 'visible' } : undefined}
    >
      <div className="d-flex align-items-center justify-content-between mb-2">
        <strong>{t('groupsPanel.groups')}</strong>
        <button type="button" className="btn btn-sm btn-primary" onClick={startCreate}>
          {t('groupsPanel.newGroup')}
        </button>
      </div>

      <div className="d-flex flex-wrap gap-2 mb-3">
        {SPORTS.map((s) => (
          <button
            key={s.key}
            className={`btn btn-sm ${filter === s.key ? 'btn-primary' : 'btn-outline-secondary'}`}
            onClick={() => setFilter(s.key)}
          >
            <span className="me-1 align-middle">{renderSportIcon(s.key, 14)}</span>
            <span className="align-middle">{s.label}</span>
          </button>
        ))}
      </div>

      <div className="row g-3">
        <div className="col-12 col-xl-4">
          <div className="border rounded p-2 bg-white" style={standalone ? { maxHeight: 'none' } : undefined}>
            {loading && <div className="text-muted">{t('groupsPanel.loadingGroups')}</div>}
            {!loading && error && <div className="text-warning small mb-2">{error}</div>}
            {!loading && filtered.length === 0 && (
              <div className="text-muted">{t('groupsPanel.noGroups')}</div>
            )}
            {!loading && filtered.length > 0 && (
              <ul className="list-group">
                {mode === 'create' && (
                  <li className="list-group-item active" style={{ cursor: 'default' }}>
                    <div className="d-flex align-items-center gap-2">
                      {renderSportIcon(form.sport, 16)}
                      <div className="fw-semibold">{t('groupsPanel.newUnsaved')}</div>
                    </div>
                  </li>
                )}
                {filtered.map((g) => (
                  <li
                    key={g._id}
                    className={`list-group-item d-flex justify-content-between align-items-center ${selected?._id === g._id ? 'active' : ''}`}
                    style={{ cursor: 'pointer' }}
                    onClick={() => {
                      setMode('view');
                      handleSelect(g);
                    }}
                  >
                    <div className="d-flex align-items-center gap-2">
                      {renderSportIcon(g.sport, 16)}
                      <div className="fw-semibold">{g.name}</div>
                    </div>
                    <span className="badge bg-light text-dark">
                      {g.members?.length || 0} {t('groupsPanel.athletesShort')}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>

        <div className="col-12 col-xl-8">
          <div
            key={`edit-${formKey}`}
            className="border rounded p-3 bg-white"
            style={standalone ? { maxHeight: 'none' } : undefined}
          >
            <div className="fw-semibold mb-2">{t('groupsPanel.detailEdit')}</div>

            {form._local && (
              <div className="alert alert-warning py-2">
                {t('groupsPanel.localGroupWarningPrefix')} <strong>{t('common.save')}</strong>{' '}
                {t('groupsPanel.localGroupWarningSuffix')}
              </div>
            )}

            <div className="mb-2">
              <label className="form-label mb-1">
                <strong>{t('groupsPanel.name')}</strong>
              </label>
              <input
                className="form-control"
                key={`name-${formKey}`}
                value={form.name}
                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                placeholder={t('groupsPanel.groupNamePlaceholder')}
                autoFocus
              />
            </div>

            <div className="mb-2">
              <label className="form-label mb-1">
                <strong>{t('groupsPanel.sport')}</strong>
              </label>
              <div className="d-flex flex-wrap gap-2">
                {SPORTS.filter((s) => s.key !== 'all').map((s) => (
                  <button
                    key={s.key}
                    type="button"
                    className={`btn btn-sm ${form.sport === s.key ? 'btn-dark' : 'btn-outline-secondary'}`}
                    onClick={() => setForm((f) => ({ ...f, sport: s.key }))}
                  >
                    <span className="me-1 align-middle">{renderSportIcon(s.key, 14)}</span>
                    {s.label}
                  </button>
                ))}
              </div>
            </div>

            <div className="mb-2">
              <label className="form-label mb-1">
                <strong>{t('groupsPanel.description')}</strong>{' '}
                <span className="text-muted small">{t('groupsPanel.optional')}</span>
              </label>
              <textarea
                className="form-control"
                rows={3}
                key={`desc-${formKey}`}
                value={form.description}
                onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
                placeholder={t('groupsPanel.descriptionPlaceholder')}
              />
            </div>

            <div className="mb-3">
              <label className="form-label mb-1">
                <strong>{t('groupsPanel.members')}</strong>
              </label>

              {membersDetailed.length > 0 && (
                <div className="d-flex flex-wrap gap-2 mb-2">
                  {membersDetailed.map((m) => (
                    <span
                      key={m.id}
                      className="badge bg-secondary d-flex align-items-center"
                      style={{ gap: 6, padding: '6px 8px' }}
                    >
                      <img
                        src={m.avatar}
                        alt={m.name}
                        width={18}
                        height={18}
                        style={{ borderRadius: '50%', objectFit: 'cover' }}
                        onError={(e) => (e.currentTarget.src = DEFAULT_AVATAR)}
                      />
                      <span>{m.name}</span>
                      <button
                        className="btn btn-sm btn-light"
                        onClick={() => removeMember(m.id)}
                        title={t('groupsPanel.remove')}
                        style={{ lineHeight: 1 }}
                      >
                        x
                      </button>
                    </span>
                  ))}
                </div>
              )}

              <div className="position-relative" ref={searchRef}>
                <input
                  className="form-control"
                  value={search}
                  onChange={(e) => {
                    setSearch(e.target.value);
                    setOpenDropdown(true);
                    setActiveIdx(0);
                  }}
                  onFocus={() => setOpenDropdown(true)}
                  onKeyDown={onSearchKey}
                  placeholder={t('groupsPanel.addAthletesPlaceholder')}
                />
                {openDropdown && (
                  <div
                    ref={dropdownRef}
                    className="position-absolute bg-white border rounded w-100 mt-1"
                    style={{ zIndex: 10, maxHeight: 240, overflow: 'auto' }}
                  >
                    {candidates.length === 0 && (
                      <div className="p-2 text-muted small">{t('groupsPanel.noResults')}</div>
                    )}
                    {candidates.map((c, i) => (
                      <div
                        key={c.id}
                        className={`d-flex align-items-center gap-2 p-2 list-group-item list-group-item-action ${i === activeIdx ? 'active' : ''}`}
                        style={{ cursor: 'pointer' }}
                        onMouseEnter={() => setActiveIdx(i)}
                        onClick={() => {
                          addMember(c);
                          setSearch('');
                          setOpenDropdown(false);
                          setActiveIdx(0);
                        }}
                      >
                        <img
                          src={c.avatar}
                          alt={c.name}
                          width={22}
                          height={22}
                          style={{ borderRadius: '50%', objectFit: 'cover' }}
                          onError={(e) => (e.currentTarget.src = DEFAULT_AVATAR)}
                        />
                        <span>{c.name}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div
                className="mt-2 p-2 border rounded text-muted small"
                onDragOver={onMembersDragOver}
                onDrop={onMembersDrop}
              >
                {t('groupsPanel.dropHint')}
              </div>

              <div className="d-flex flex-wrap gap-2 mt-2 mb-2">
                <button
                  type="button"
                  className="btn btn-sm btn-outline-primary"
                  onClick={() => setShowAllAthletes((s) => !s)}
                >
                  {showAllAthletes
                    ? t('groupsPanel.hideList')
                    : t('groupsPanel.viewAll', { count: allAthletes.length })}
                </button>
                <button type="button" className="btn btn-sm btn-outline-success" onClick={addAllMembers}>
                  {t('groupsPanel.addAll')}
                </button>
                <button type="button" className="btn btn-sm btn-outline-secondary" onClick={clearAllMembers}>
                  {t('groupsPanel.removeAll')}
                </button>
              </div>

              {showAllAthletes && (
                <div className="border rounded" style={{ maxHeight: 320, overflow: 'auto' }}>
                  {allAthletes.map((a) => {
                    const checked = (form.members || []).map(String).includes(String(a.id));
                    return (
                      <label
                        key={a.id}
                        className="d-flex align-items-center gap-2 p-2 border-bottom"
                        style={{ cursor: 'pointer', userSelect: 'none' }}
                      >
                        <input
                          type="checkbox"
                          className="form-check-input"
                          checked={checked}
                          onChange={(e) => (e.target.checked ? addMember(a) : removeMember(a.id))}
                          onClick={(e) => e.stopPropagation()}
                        />
                        <img
                          src={a.avatar}
                          alt={a.name}
                          width={24}
                          height={24}
                          style={{ borderRadius: '50%', objectFit: 'cover' }}
                          onError={(e) => (e.currentTarget.src = DEFAULT_AVATAR)}
                        />
                        <span className="flex-grow-1">{a.name}</span>
                        {checked && <span className="badge bg-success">{t('groupsPanel.member')}</span>}
                      </label>
                    );
                  })}
                  {allAthletes.length === 0 && (
                    <div className="p-2 text-muted">{t('groupsPanel.noAthletes')}</div>
                  )}
                </div>
              )}
            </div>

            <div className="d-flex flex-wrap align-items-center gap-2">
              <button className="btn btn-success" disabled={!isDirty || saving} onClick={save}>
                {saving ? t('groupsPanel.saving') : t('common.save')}
              </button>

              <button
                className="btn btn-primary"
                onClick={() => {
                  if (!form.members || form.members.length === 0) {
                    alert(t('groupsPanel.addAtLeastOneAthlete'));
                    return;
                  }
                  setShowApply(true);
                }}
              >
                {t('groupsPanel.applyPlan')}
              </button>

              <button className="btn btn-outline-secondary" onClick={saveAsNew} disabled={saving}>
                {t('groupsPanel.saveAsNew')}
              </button>
              <button className="btn btn-outline-danger ms-auto" onClick={handleDelete} disabled={!form._id}>
                {t('common.delete')}
              </button>
              {savedMsg && <span className="text-success small">{savedMsg}</span>}
              {!savedMsg && !isDirty && <span className="text-muted small">{t('groupsPanel.noChanges')}</span>}
            </div>

            {applyResult && (
              <div className={`alert ${applyResult?.okCount ? 'alert-info' : 'alert-warning'} mt-3 mb-0`}>
                {applyResult.message ||
                  (applyResult.okCount
                    ? t('groupsPanel.appliedToAthletes', {
                        okCount: applyResult.okCount,
                        total: applyResult.total || form.members.length,
                      })
                    : t('groupsPanel.couldNotApplyTemplate'))}
                {applyResult.errors?.length ? (
                  <details className="mt-2">
                    <summary className="cursor-pointer">{t('groupsPanel.viewDetails')}</summary>
                    <ul className="mb-0">
                      {applyResult.errors.map((e, i) => (
                        <li key={i} className="small">
                          {typeof e === 'string' ? e : e?.message || JSON.stringify(e)}
                        </li>
                      ))}
                    </ul>
                  </details>
                ) : null}
              </div>
            )}
          </div>
        </div>
      </div>

      <GroupApplyPlanModal
        open={showApply}
        onClose={() => setShowApply(false)}
        trainerId={trainerId}
        athleteIds={(form.members || []).map(String)}
        onApplied={(res) => {
          setApplyResult(
            res || {
              okCount: 0,
              total: (form.members || []).length,
              message: t('groupsPanel.couldNotApplyTemplate'),
            }
          );
          setShowApply(false);
        }}
      />
    </div>
  );
};

export default GroupsPanel;